import { Component, ElementRef, forwardRef, input, viewChild } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { DefaultControlValueAccessor } from '@coin/shared/core/util-helpers';
import { MatTooltip } from '@angular/material/tooltip';
import { MatFormFieldClass } from '../../input.types';

@Component({
  selector: 'coin-v2-number-input',
  templateUrl: './v2-number-input.component.html',
  imports: [MatFormFieldModule, MatInput, ReactiveFormsModule, FormsModule, MatTooltip],
  styleUrls: ['./v2-number-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => V2NumberInputComponent),
      multi: true
    }
  ]
})
export class V2NumberInputComponent extends DefaultControlValueAccessor<number> {
  readonly label = input<string>(undefined);
  readonly placeholder = input<string>(undefined);
  readonly icon = input<string>(undefined);
  readonly matFormFieldClass = input<MatFormFieldClass>('customer-grey');
  readonly hideInputArrows = input(false);
  readonly step = input<number>(undefined);
  /** When set to `true`, value updates be forced to stay within step range regardless of what is entered in the input */
  readonly enforceStep = input(false);
  // TODO: min/max should be supplied by form validators, not by inputs
  readonly min = input<number>(undefined);
  readonly max = input<number>(undefined);
  /** When set to `true`, value updates be forced to stay within min/max range regardless of what is entered in the input */
  readonly enforceMinMax = input(false);
  /**
   * * `input`: (default) the form value will update every time the input is updated
   * * `blur` the form value will only update after the input loses focus */
  readonly updateOn = input<'input' | 'blur'>('input');

  private readonly numberInput = viewChild<ElementRef<HTMLInputElement>>('numberInput');

  public focus(): void {
    this.numberInput()?.nativeElement?.focus();
  }

  private updateValue(value: number): void {
    let enforcedValue = value;

    const step = this.step();
    if (this.enforceStep() && step && enforcedValue % step !== 0) {
      enforcedValue = Math.ceil(enforcedValue / step) * step;
    }

    if (this.enforceMinMax()) {
      const min = this.min();
      const max = this.max();
      if (min !== null && enforcedValue < min) {
        enforcedValue = min;
      } else if (max !== null && enforcedValue > max) {
        enforcedValue = max;
      }
    }

    this.value = enforcedValue;
    this.cd.markForCheck();
  }

  protected onInputChange(value: number) {
    if (this.updateOn() === 'input' && value !== this.value) {
      this.updateValue(value);
    }
  }

  protected onInputBlur(stringValue: string) {
    const value = ['', null, undefined].includes(stringValue) ? null : Number(stringValue);
    this.onTouch();
    if (this.updateOn() === 'blur' && value !== this.value) {
      this.updateValue(value);
    }
  }
}
